import { t as uiText } from '../../i18n';
/**
 * FeatureKey の階層表示定義。
 *
 * 「どの FeatureKey が画面上のどの設定に対応するか」を 1 箇所にまとめる。
 * Service Admin の Offer Entitlement カタログ表示、および将来の Workspace Override
 * 編集 UI から参照する。判定ロジックは持たない（表示メタデータのみ）。
 *
 * FeatureKey の正本は functions/src/entitlements/types.ts の FEATURE_KEYS。
 * キーを増やしたらこのファイルにもノードを追加すること（assertFeatureHierarchyCoverage が検知する）。
 */
import type { FeatureKey } from './entitlements';

export type FeatureNode = {
  key: FeatureKey;
  /** カタログ上の表示名 */
  label: string;
  /** 画面上のどこにある設定か */
  uiPath: string;
  children?: FeatureNode[];
};

export type FeatureSection = {
  sectionLabel: string;
  nodes: FeatureNode[];
};

/**
 * 常に ON で、ON/OFF の対象にしない FeatureKey。
 *
 * FEATURE_KEYS からは外さない（operationFeatureMap が analytics を
 * analyzeVisitorVoice.* / identifyAnswerQualityGaps.* の判定に使う）。
 * カタログと Override 編集には出さないため、階層定義にも載せない。
 */
export const ALWAYS_ON_FEATURE_KEYS: readonly FeatureKey[] = ['aiSearch', 'analytics'];

export const FEATURE_HIERARCHY: FeatureSection[] = [
  {
    get sectionLabel() { return uiText("Shared foundation (NCN / NCG)"); },
    nodes: [
      {
        key: 'members',
        get label() { return uiText("Member limit"); },
        get uiPath() { return uiText("Member management"); },
      },
      {
        key: 'storage',
        get label() { return uiText("Storage limit"); },
        get uiPath() { return uiText("File tab / storage usage"); },
      },
      {
        key: 'company',
        get label() { return uiText("Organization account management"); },
        get uiPath() { return uiText("User menu → Organization account management (/admin)"); },
      },
      {
        key: 'workspace.linkage',
        get label() { return uiText("Linking settings"); },
        get uiPath() { return uiText("Workspace settings → Association (organization / parent workspace)"); },
      },
      {
        key: 'workspace.icon',
        get label() { return uiText("Workspace icon"); },
        get uiPath() { return uiText("Workspace settings → Icon settings"); },
      },
      {
        key: 'workspace.baseColor',
        get label() { return uiText("Base color"); },
        get uiPath() { return uiText("Workspace settings → Base color (15 colors)"); },
      },
      {
        key: 'notification.page',
        get label() { return uiText("Page notifications"); },
        get uiPath() { return uiText("Notification settings → Page notifications (on create / on update)"); },
      },
      {
        key: 'notification.thread',
        get label() { return uiText("Thread notifications"); },
        get uiPath() { return uiText("Notification settings → Thread notifications (created / content added / comment / status change)"); },
      },
      {
        key: 'notification.slack',
        get label() { return uiText("Slack integration"); },
        get uiPath() { return uiText("Notification settings → Open Slack integration"); },
      },
    ],
  },
  {
    get sectionLabel() { return uiText("AI credits"); },
    nodes: [
      {
        key: 'credits.agent',
        get label() { return uiText("Included in the internal AI quota"); },
        get uiPath() { return uiText("Home settings AI chat"); },
      },
      {
        key: 'credits.internalRag',
        get label() { return uiText("Internal AI monthly credits"); },
        get uiPath() { return uiText("Internal AI search, settings AI, and other admin AI"); },
      },
      {
        key: 'credits.publicRag',
        get label() { return uiText("Public AI monthly credits"); },
        get uiPath() { return uiText("Questions from the public page / widget"); },
      },
    ],
  },
  {
    get sectionLabel() { return uiText("Public RAG (all items off on NCN)"); },
    nodes: [
      {
        key: 'publicRag',
        get label() { return uiText("Publish settings"); },
        get uiPath() { return uiText("RAG settings group → Public settings tab"); },
        children: [
          {
            key: 'publicRag.webPages',
            get label() { return uiText("Publish page on the web"); },
            get uiPath() { return uiText("Public page settings → Publish page on the web"); },
          },
          {
            key: 'publicRag.ogMeta',
            get label() { return uiText("OG metadata settings"); },
            get uiPath() { return uiText("Public page settings → Public page management → OG metadata (title / description / image)"); },
          },
          {
            key: 'multilingual',
            get label() { return uiText("Multilingual"); },
            get uiPath() { return uiText("Public site settings → Multilingual on/off (add languages / switch primary)"); },
          },
          {
            key: 'publicRag.tabAppearance',
            get label() { return uiText("Favicon / tab title"); },
            get uiPath() { return uiText("Public site settings → Tab settings"); },
          },
          {
            key: 'publicRag.font',
            get label() { return uiText("Font settings"); },
            get uiPath() { return uiText("Public site settings → General (English primary / Japanese font)"); },
          },
          {
            key: 'publicRag.header',
            get label() { return uiText("Header settings"); },
            get uiPath() { return uiText("Public site settings → Header (background color / height / shadow / fixed / title / max width)"); },
            children: [
              {
                key: 'publicRag.header.logo',
                get label() { return uiText("Logo settings"); },
                get uiPath() { return uiText("Header settings → Logo settings (image / height / link destination / new tab)"); },
              },
              {
                key: 'publicRag.header.links',
                get label() { return uiText("Header link"); },
                get uiPath() { return uiText("Header settings → Link settings (+ Add link)"); },
              },
            ],
          },
          {
            key: 'publicRag.designColor',
            get label() { return uiText("Design color"); },
            get uiPath() { return uiText("Content settings → Colors for main / link / main text / sub / keywords"); },
          },
          {
            key: 'publicRag.titleIcon',
            get label() { return uiText("Main title icon"); },
            get uiPath() { return uiText("Content settings → Main title icon (image / size / position / recolor)"); },
          },
          {
            key: 'publicRag.contentText',
            get label() { return uiText("Content text"); },
            get uiPath() { return uiText("Content settings → Title / subtitle / placeholder"); },
          },
          {
            key: 'publicRag.privacyNotice',
            get label() { return uiText("Personal information notice"); },
            get uiPath() { return uiText("Content settings → Personal information notice (wording / position / padding)"); },
            children: [
              {
                key: 'publicRag.privacyNotice.consent',
                get label() { return uiText("Consent check before input"); },
                get uiPath() { return uiText("Personal information notice → consent checkbox before input"); },
              },
            ],
          },
          {
            key: 'publicRag.textSizeControl',
            get label() { return uiText("Text size toggle"); },
            get uiPath() { return uiText("Content settings → Show the text size toggle (+ default)"); },
          },
          {
            key: 'publicRag.sessionHistory',
            get label() { return uiText("History and new chat"); },
            get uiPath() { return uiText("Content settings → Show history and new conversation"); },
          },
          {
            key: 'voiceInput',
            get label() { return uiText("Voice input"); },
            get uiPath() { return uiText("Public site settings → Content → Enable voice input"); },
          },
          {
            key: 'publicRag.footer',
            get label() { return uiText("Footer settings"); },
            get uiPath() { return uiText("Public site settings → Footer (background / top border / text / color)"); },
          },
        ],
      },
    ],
  },
  {
    get sectionLabel() { return uiText("Floating widget (all items OFF on NCN)"); },
    nodes: [
      {
        key: 'widget',
        get label() { return uiText("Enable widget"); },
        get uiPath() { return uiText("Publish settings → Widget tab"); },
        children: [
          {
            key: 'widget.buttonText',
            get label() { return uiText("Button style: Text"); },
            get uiPath() { return uiText("Button style → Text (label / color / size / weight / direction / upright-in-vertical)"); },
          },
          {
            key: 'widget.buttonImage',
            get label() { return uiText("Button style: Image"); },
            get uiPath() { return uiText("Button style → Image (button image / display mode / how it is shown)"); },
          },
          {
            key: 'widget.icon',
            get label() { return uiText("Icon"); },
            get uiPath() { return uiText("Button style → Icon (image / size / position / color)"); },
          },
          {
            key: 'widget.closeButton',
            get label() { return uiText("Close button"); },
            get uiPath() { return uiText("Show close button (+ close color / background / opacity / border)"); },
          },
          {
            key: 'widget.tooltip',
            get label() { return uiText("Tooltip"); },
            get uiPath() { return uiText("Show tooltip (+ text / color / alignment / width)"); },
          },
          {
            key: 'widget.position',
            get label() { return uiText("Display position"); },
            get uiPath() { return uiText("Position (right-edge anchor / base position / fine tune) + other settings (width / offset)"); },
          },
          {
            key: 'widget.mobileCallButton',
            get label() { return uiText("Separate smartphone design"); },
            get uiPath() { return uiText("Call button settings → Separate smartphone design"); },
          },
          {
            key: 'widget.apiToken',
            label: 'API Token',
            get uiPath() { return uiText("API token & security settings (issue token / allowed embed origins / enable)"); },
          },
          {
            key: 'widget.cacheMode',
            get label() { return uiText("Immediate / Cached"); },
            get uiPath() { return uiText("Embed code → Dynamic settings type (live ⇔ cache)"); },
          },
        ],
      },
    ],
  },
  {
    get sectionLabel() { return uiText("Site crawler (all items OFF on NCN)"); },
    nodes: [
      {
        key: 'crawler',
        get label() { return uiText("Site crawler"); },
        get uiPath() { return uiText("Publish settings → Crawler tab"); },
        children: [
          {
            key: 'crawler.bulkRegister',
            get label() { return uiText("Register child pages in bulk"); },
            get uiPath() { return uiText("Crawler settings → Bulk-register subpages tab"); },
          },
          {
            key: 'crawler.urlList',
            get label() { return uiText("Crawl specified URLs"); },
            get uiPath() { return uiText("Crawler settings → Crawl by URL tab (CSV import / register URLs / validate)"); },
          },
          {
            key: 'crawler.schedule',
            get label() { return uiText("Recurring schedule"); },
            get uiPath() { return uiText("Crawler dashboard → Recurring schedule (frequency / weekday / day / time)"); },
          },
          {
            key: 'fileIngest',
            get label() { return uiText("File import"); },
            get uiPath() { return uiText("Import from file / text (PDF, etc.)"); },
          },
        ],
      },
    ],
  },
  {
    get sectionLabel() { return uiText("Answer filter (all items off on NCN)"); },
    nodes: [
      {
        key: 'answerFilter',
        get label() { return uiText("Enable answer filter"); },
        get uiPath() { return uiText("Publish settings → Answer filter tab"); },
        children: [
          {
            key: 'answerFilter.buttons',
            get label() { return uiText("Show filter button"); },
            get uiPath() { return uiText("Answer filter → Show filter button"); },
          },
          {
            key: 'answerFilter.urlPrefix',
            get label() { return uiText("URL prefix filter"); },
            get uiPath() { return uiText("Answer filter → URL prefix filter"); },
          },
          {
            key: 'answerFilter.hashtag',
            get label() { return uiText("Hashtag filter"); },
            get uiPath() { return uiText("Answer filter → Hashtag filter"); },
          },
        ],
      },
    ],
  },
  {
    get sectionLabel() { return uiText("DRS (all items off on NCN)"); },
    nodes: [
      {
        key: 'drs',
        label: 'DRS',
        get uiPath() { return uiText("RAG settings group → DRS tab"); },
        children: [
          {
            key: 'drs.aiGenerate',
            get label() { return uiText("DRS AI generation"); },
            get uiPath() { return uiText("DRS → Generate DRS with AI (per page / bulk)"); },
          },
          {
            key: 'drs.manualEdit',
            get label() { return uiText("Add or edit DRS manually"); },
            get uiPath() { return uiText("DRS → + Add DRS manually (template / slot editing)"); },
          },
          {
            key: 'drs.multilingual',
            get label() { return uiText("DRS multilingual"); },
            get uiPath() { return uiText("DRS → Add a language / Create DRS in a specified language"); },
          },
          {
            key: 'drs.suggest',
            get label() { return uiText("Suggestions"); },
            get uiPath() { return uiText("DRS → Suggestions tab"); },
          },
          {
            key: 'drs.keyword',
            get label() { return uiText("Keywords"); },
            get uiPath() { return uiText("DRS → Keywords tab (+ keyword candidate display text in content settings)"); },
          },
        ],
      },
    ],
  },
  {
    get sectionLabel() { return uiText("OGA (all items off on NCN)"); },
    nodes: [
      {
        key: 'oga',
        label: 'OGA',
        get uiPath() { return uiText("RAG settings group → OGA tab"); },
        children: [
          {
            key: 'oga.answerPage',
            get label() { return uiText("Suggest answer pages"); },
            get uiPath() { return uiText("OGA settings → Show answer pages (+ announcement when the answer page is shown)"); },
          },
          {
            key: 'oga.inlineSourceUrls',
            get label() { return uiText("Also show links in the page body"); },
            get uiPath() { return uiText("OGA settings → Also show links in the page body"); },
          },
          {
            key: 'oga.crawlReferencedUrls',
            get label() { return uiText("Read referenced links and add a follow-up answer"); },
            get uiPath() { return uiText("OGA settings → Read referenced links and add supplementary answers"); },
          },
          {
            key: 'oga.contactHeading',
            get label() { return uiText("Header text display"); },
            get uiPath() { return uiText("OGA settings → Show header text (+ display text settings)"); },
          },
          {
            key: 'oga.contactUrl',
            get label() { return uiText("Show inquiry page link"); },
            get uiPath() { return uiText("OGA settings → Show a contact page link (guidance text / label / display title / URL)"); },
          },
          {
            key: 'oga.contactPhone',
            get label() { return uiText("Phone number display"); },
            get uiPath() { return uiText("OGA settings → Show phone number (guidance text / label / phone number)"); },
          },
          {
            key: 'oga.customLink',
            get label() { return uiText("Show custom link"); },
            get uiPath() { return uiText("OGA settings → Show a custom link (guidance text / label / display title / URL)"); },
          },
        ],
      },
    ],
  },
];

/**
 * `isEnabled` が false のノードを畳み落とした階層を返す。
 *
 * NCN は大半の項目が対象外で OFF になるため、そのまま並べると関係ある項目が埋もれる。
 * 子孫が 1 つでも残るノードは階層を保つために残し、ノードが空になったセクションは落とす。
 */
export function filterHierarchy(
  sections: FeatureSection[],
  isEnabled: (key: FeatureKey) => boolean
): FeatureSection[] {
  const walk = (nodes: FeatureNode[]): FeatureNode[] => {
    const out: FeatureNode[] = [];
    for (const node of nodes) {
      const children = node.children ? walk(node.children) : [];
      if (isEnabled(node.key) || children.length > 0) {
        out.push({ ...node, children: children.length > 0 ? children : undefined });
      }
    }
    return out;
  };

  return sections
    .map((section) => ({ ...section, nodes: walk(section.nodes) }))
    .filter((section) => section.nodes.length > 0);
}

/**
 * FeatureKey から親チェーン（近い順）を返す。
 *
 * 親が OFF なら子も OFF、という連動は判定側の責務（resolveEffectiveEntitlements は
 * 各キーを独立に返す）。その判定に使う。階層に載っていないキーは空配列。
 */
export function getFeatureAncestors(key: FeatureKey): FeatureKey[] {
  const walk = (nodes: FeatureNode[], trail: FeatureKey[]): FeatureKey[] | null => {
    for (const node of nodes) {
      if (node.key === key) return trail;
      if (node.children) {
        const found = walk(node.children, [node.key, ...trail]);
        if (found) return found;
      }
    }
    return null;
  };

  for (const section of FEATURE_HIERARCHY) {
    const found = walk(section.nodes, []);
    if (found) return found;
  }
  return [];
}

/** FEATURE_HIERARCHY に含まれる全 FeatureKey を平坦化して返す */
export function flattenFeatureHierarchy(
  sections: FeatureSection[] = FEATURE_HIERARCHY
): FeatureNode[] {
  const out: FeatureNode[] = [];
  const walk = (nodes: FeatureNode[]): void => {
    for (const node of nodes) {
      out.push(node);
      if (node.children) walk(node.children);
    }
  };
  for (const section of sections) walk(section.nodes);
  return out;
}
